"use client";

import { motion } from "framer-motion";
import {
  Stethoscope, Activity, Bone, Eye, Sparkles,
  Sun, HeartPulse, Dumbbell, SmilePlus,
} from "lucide-react";

const specialties = [
  {
    icon: SmilePlus,
    title: "Dental",
    description: "Automate appointment reminders, hygiene recall, and new patient intake. Inbound GPT handles insurance questions and booking so your front desk stays focused on in-office patients.",
  },
  {
    icon: Activity,
    title: "Physical Therapy",
    description: "Manage recurring session bookings, handle cancellations, and answer questions about treatment plans. Inbound GPT keeps your therapy schedule full and patients on track.",
  },
  {
    icon: Bone,
    title: "Chiropractic",
    description: "Handle new patient consultations, manage adjustment schedules, and answer insurance and pricing questions — all without tying up your front desk staff.",
  },
  {
    icon: Eye,
    title: "Optometry",
    description: "Schedule eye exams, manage contact lens reorders, and field patient inquiries about vision coverage. Inbound GPT keeps your optical practice running smoothly.",
  },
  {
    icon: Sparkles,
    title: "Cosmetic Surgery",
    description: "Answer consultation requests, manage pre-op and post-op scheduling, and handle sensitive patient inquiries with discretion and professionalism.",
  },
  {
    icon: Sun,
    title: "Dermatology",
    description: "Book skin checks, manage follow-up appointments, and handle prescription refill requests. Inbound GPT triages urgent concerns and routes them to your team.",
  },
  {
    icon: HeartPulse,
    title: "Internal Medicine",
    description: "Manage complex scheduling for multi-provider practices, handle referral coordination, and answer patient questions about lab results and follow-ups.",
  },
  {
    icon: Dumbbell,
    title: "Sports Medicine",
    description: "Coordinate injury evaluations, manage rehab session bookings, and handle athlete intake forms. Inbound GPT keeps your sports clinic moving at game speed.",
  },
  {
    icon: Stethoscope,
    title: "Orthodontics",
    description: "Schedule adjustments, manage retainer check-ups, and answer questions about treatment timelines and payment plans — keeping parents and patients informed.",
  },
];

export function UseCases() {
  return (
    <section id="case-studies" data-testid="section-case-studies" className="relative py-24 sm:py-32">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-primary/3 blur-[100px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl sm:text-4xl font-bold tracking-tight"
          >
            See Inbound GPT <span className="text-primary">At Work</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto"
          >
            Explore real Inbound GPTs generated by practices like yours.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {specialties.map((spec, index) => (
            <motion.div
              key={spec.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.06 }}
              className="group rounded-md border border-border bg-card p-6 hover-elevate"
              data-testid={`card-specialty-${spec.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
                  <spec.icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-lg font-semibold">{spec.title}</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">{spec.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
